import React, { createContext, useState, useContext, useCallback } from 'react';
import api from '../services/api';
import { API_ENDPOINTS } from '../utils/constants';
import { storage } from '../utils/helpers';

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const isDemoMode = useCallback(() => {
    return storage.get('demo_mode') === true;
  }, []);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isDemoMode() ? API_ENDPOINTS.DEMO.LOCATIONS : API_ENDPOINTS.LOCATIONS.LIST;
      const response = await api.get(endpoint);
      setLocations(response.data.locations || []);
      setSelectedLocations(response.data.selectedLocations || []);
      return response.data.locations || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      // For demo mode, return default location
      if (isDemoMode()) {
        const demoLocations = [{
          id: 'demo-location-1',
          name: 'Downtown Cafe & Bistro',
          address: '123 Main Street, New York, NY 10001'
        }];
        setLocations(demoLocations);
        setSelectedLocations(demoLocations);
        return demoLocations;
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isDemoMode]);

  const selectLocation = async (location) => {
    if (isDemoMode()) {
      setSelectedLocations([...selectedLocations, location]);
      return { success: true };
    }
    
    try {
      await api.post(API_ENDPOINTS.LOCATIONS.SELECT, { locationId: location.id });
      setSelectedLocations([...selectedLocations, location]);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const removeLocation = async (locationId) => {
    if (isDemoMode()) {
      setSelectedLocations(selectedLocations.filter(loc => loc.id !== locationId));
      return { success: true };
    }
    
    try {
      await api.post(API_ENDPOINTS.LOCATIONS.REMOVE, { locationId });
      setSelectedLocations(selectedLocations.filter(loc => loc.id !== locationId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    locations,
    selectedLocations,
    loading,
    fetchLocations,
    selectLocation,
    removeLocation
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export const useLocations = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocations must be used within LocationProvider');
  return context;
};

export default LocationContext;
